/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState } from "react";
import { BookOpen, Calendar, MapPin, Sparkles, Shirt, ChevronRight, Flower2, Clock } from "lucide-react";
import { WEDDING_STORIES } from "../data";
import { WeddingStory } from "../types";

export default function StoriesGrid() {
  const [activeStory, setActiveStory] = useState<WeddingStory | null>(null);

  return (
    <section className="bg-[#F5F2ED] text-[#1A1A1A] py-20 px-6 border-t border-[#1A1A1A]" id="stories-section">
      <div className="max-w-7xl mx-auto">
        
        {/* Editorial Subheader */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#c4a482] font-display flex items-center justify-center gap-1.5 mb-3">
            <BookOpen size={11} /> The House Journal
          </span>
          <h2 className="font-serif text-[42px] sm:text-[52px] font-extralight tracking-tight leading-none text-[#1A1A1A]">
            Cinematic Wedding Chronicles
          </h2>
          <div className="w-16 h-[1px] bg-[#1A1A1A] mx-auto my-6"></div>
          <p className="font-sans font-light text-xs sm:text-sm text-[#7f7d78] max-w-lg mx-auto leading-relaxed uppercase tracking-wider">
            Step inside our detailed, high-fashion campaign stories. Each wedding is handled as a distinct, tailored art editorial.
          </p>
        </div>

        {/* Stories Listing (Toggle mode or Full View mode) */}
        {!activeStory ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {WEDDING_STORIES.map((story) => (
              <div
                key={story.id}
                id={`story-card-${story.id}`}
                onClick={() => setActiveStory(story)}
                className="bg-[#E8E4DF]/40 border border-[#1A1A1A] flex flex-col justify-between group cursor-pointer hover:bg-[#E8E4DF]/80 hover:shadow-none transition-all duration-700"
              >
                {/* Visual Cover Thumbnail */}
                <div className="overflow-hidden bg-[#E8E4DF] relative aspect-[4/5] sm:aspect-auto sm:h-72 border-b border-[#1A1A1A]">
                  <div className="grain-overlay absolute inset-0 z-10"></div>
                  
                  {story.coverUrl && (
                    <img
                      src={story.coverUrl}
                      alt={story.couple}
                      referrerPolicy="no-referrer"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 filter grayscale-[25%] sepia-[15%] contrast-[1.05]"
                    />
                  )}

                  {/* Subtle palette circles in corner */}
                  <div className="absolute top-4 left-4 z-20 flex space-x-1 p-1 bg-[#F5F2ED]/95 rounded-none border border-[#1A1A1A]">
                    {story.paletteColors.map((color, idx) => (
                      <span
                        key={idx}
                        className="w-2.5 h-2.5 rounded-full border border-[#1A1A1A]/20"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>

                  {/* Render descriptive background details */}
                  <div className="absolute inset-0 bg-stone-950/65 flex flex-col justify-end p-6 text-white group-hover:bg-stone-950/75 transition-all duration-700 z-10">
                    <span className="text-[8px] tracking-[0.3em] uppercase text-[#c4a482] font-semibold mb-2">
                      {story.region}
                    </span>
                    <h3 className="font-serif text-[24px] font-light leading-tight group-hover:tracking-wide transition-all duration-700">
                      {story.couple}
                    </h3>
                    <p className="text-[10px] text-zinc-300 font-light mt-2 line-clamp-3">
                      {story.narrativeIntro}
                    </p>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-center text-[10px] text-[#7f7d78] uppercase tracking-wider font-light">
                    <span className="flex items-center gap-1">
                      <Calendar size={11} className="text-[#1A1A1A]" />
                      {story.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={11} className="text-[#1A1A1A]" />
                      {story.locationName}
                    </span>
                  </div>

                  <button
                    id={`btn-read-story-${story.id}`}
                    className="text-[10.5px] uppercase tracking-[0.25em] text-[#1A1A1A] font-semibold group-hover:text-[#c4a482] transition duration-300 flex items-center gap-1"
                  >
                    <span>Read Chronicle</span>
                    <ChevronRight size={12} className="group-hover:translate-x-1 duration-300" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Detailed Multi-Section Story Read View */
          <div className="bg-[#F5F2ED] border border-[#1A1A1A] p-6 sm:p-12 space-y-12 relative overflow-hidden" id="active-story-panel">
            {/* Dynamic Grain overlay */}
            <div className="grain-overlay absolute inset-0"></div>

            {/* Back to Stories list navigation link */}
            <button
              id="btn-close-story"
              onClick={() => setActiveStory(null)}
              className="absolute top-6 right-6 text-xs uppercase tracking-widest text-neutral-500 hover:text-black hover:font-semibold transition pb-1 border-b border-[#1A1A1A]/20 cursor-pointer z-10"
            >
              ← Back to Overview
            </button>

            {/* Column 1: Header Narrative Info */}
            <div className="max-w-3xl space-y-6">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#c4a482] font-semibold block">
                {activeStory.region} • {activeStory.date}
              </span>
              <h3 className="font-serif text-[42px] sm:text-[62px] font-extralight tracking-tight leading-none text-[#1A1A1A]">
                {activeStory.couple}
              </h3>
              <p className="text-xl sm:text-2xl font-serif font-light text-neutral-800 tracking-wide leading-relaxed pl-4 border-l-2 border-[#1A1A1A] py-1 italic">
                "{activeStory.quote}"
              </p>
            </div>

            {/* Horizontal Split Specs Matrix */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-b border-[#1A1A1A] py-8 text-[#1A1A1A]">
              {/* Left Column Description (7 Columns) */}
              <div className="md:col-span-8 space-y-6">
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#1A1A1A]/60 font-semibold block">
                  The Editorial Narrative
                </span>
                <p className="text-sm font-light leading-relaxed text-neutral-800 uppercase tracking-wider">
                  {activeStory.narrativeIntro}
                </p>
                <p className="text-sm font-light leading-relaxed text-neutral-700 font-serif text-[15px]">
                  {activeStory.narrativeBody}
                </p>
              </div>

              {/* Right Column Specs Card (4 Columns) */}
              <div className="md:col-span-4 bg-[#E8E4DF]/50 border border-[#1A1A1A] p-6 space-y-6 self-start">
                <div className="space-y-2">
                  <span className="block text-[9px] uppercase tracking-[0.2em] text-[#1A1A1A]/60 font-semibold">
                    The Campaign Swatch
                  </span>
                  <div className="flex items-center space-x-3">
                    <div className="flex -space-x-1.5">
                      {activeStory.paletteColors.map((col, i) => (
                        <span
                          key={i}
                          className="w-5 h-5 rounded-full border border-black/15 block"
                          style={{ backgroundColor: col }}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-light text-[#1A1A1A] tracking-wider">{activeStory.paletteName}</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="block text-[9px] uppercase tracking-[0.2em] text-[#1A1A1A]/60 font-semibold">
                    Vibe Atmosphere
                  </span>
                  <span className="text-xs font-light text-neutral-800 block">{activeStory.atmosphere}</span>
                </div>

                <div className="border-t border-[#1A1A1A]/30 pt-4 space-y-4 text-xs font-light">
                  <span className="block text-[9px] uppercase tracking-[0.2em] text-[#1A1A1A] font-semibold">
                    Styling Blueprint
                  </span>

                  <div className="flex items-start gap-2 text-[11px]">
                    <Shirt size={13} className="text-[#1A1A1A] shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold block text-[10px] text-[#1A1A1A]/80">Couture Gown</span>
                      <span className="text-neutral-700">{activeStory.stylingDetails.gowns}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-[11px]">
                    <Sparkles size={13} className="text-[#1A1A1A] shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold block text-[10px] text-[#1A1A1A]/80">Tailoring & Cut</span>
                      <span className="text-neutral-700">{activeStory.stylingDetails.tailoring}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-[11px]">
                    <Flower2 size={13} className="text-[#1A1A1A] shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold block text-[10px] text-neutral-800">Floral Arrangements</span>
                      <span className="text-neutral-600">{activeStory.stylingDetails.floralDesign}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section: Chronological Wedding Chapters */}
            <div className="space-y-6">
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#1A1A1A]/60 font-semibold block">
                The Wedding Chapters Agenda
              </span>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {activeStory.keyChapters.map((ch, idx) => (
                  <div key={idx} className="border border-[#1A1A1A] p-5 bg-[#E8E4DF]/30 flex flex-col justify-between h-40">
                    <div>
                      <div className="flex items-center space-x-1.5 text-[#1A1A1A] text-[10px] uppercase font-display font-semibold mb-2">
                        <Clock size={11} />
                        <span>{ch.time}</span>
                      </div>
                      <h4 className="font-serif text-[16px] font-medium text-[#1A1A1A] mb-1">
                        {ch.title}
                      </h4>
                    </div>
                    <p className="text-[11px] text-neutral-600 font-light leading-relaxed">
                      {ch.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Nav CTA */}
            <div className="pt-6 border-t border-[#1A1A1A] flex justify-end">
              <button
                id="btn-bottom-back"
                onClick={() => setActiveStory(null)}
                className="w-full sm:w-auto bg-[#1A1A1A] text-[#F5F2ED] text-xs uppercase tracking-[0.2em] px-8 py-3.5 hover:bg-neutral-800 transition duration-500 font-display cursor-pointer"
              >
                Return to Stories Overview
              </button>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
